import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import {
  ArrowRight,
  Basket3,
  ChevronDown,
  ChevronUp,
  Check2,
  Check2Circle,
  Clock,
  Cup,
  EggFried,
  HouseDoorFill,
  LightningCharge,
  People,
  PlusCircle,
  Printer,
  Share,
  ThermometerHigh,
  Tools
} from 'react-bootstrap-icons';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  formatIngredientLabel,
  getRecipeImageCandidates,
  formatOptionalIngredientNote,
  formatPackageSize,
  getRecipeImagePosition,
  formatTimeRange,
  formatUsage,
  normalize,
  normalizeSection
} from '../utils/recipes';
import { fetchJsonOrThrow } from '../utils/fetchJson';
import { loadProfile } from '../utils/profileStorage';
import {
  PANTRY_STORAGE_KEY,
  consumePantryEntry,
  createPantryEntry,
  getPantryKey,
  loadPantryState,
  pantryStateToItems,
  savePantryState,
} from '../utils/pantryStorage';
import {
  convertIngredientQuantityAmount,
  getPantryItemQuantity,
  getRequiredIngredientQuantity,
  pantryItemCoversIngredient,
} from '../utils/recipePricing';
import api_url from '../config/api_url';
import FavoriteButton from '../components/FavoriteButton';

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 420'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='0' y2='1'%3E%3Cstop offset='0%25' stop-color='%23f7ecdf'/%3E%3Cstop offset='100%25' stop-color='%23e6d7c4'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='640' height='420' fill='url(%23g)'/%3E%3C/svg%3E";

function splitGearItems(gearText) {
  return normalize(gearText)
    .split(/,| og | eller |\//i)
    .map((item) => item.trim())
    .filter(Boolean);
}

function resolveGearIcon(gearItem) {
  const value = normalize(gearItem).toLowerCase();
  if (/pande|wok/.test(value)) {
    return EggFried;
  }
  if (/gryde|potte/.test(value)) {
    return Cup;
  }
  if (/ovn/.test(value)) {
    return ThermometerHigh;
  }
  if (/blender/.test(value)) {
    return LightningCharge;
  }
  return Tools;
}

function parseInstructionSteps(instructionsText) {
  const compactText = normalize(instructionsText).replace(/\s+/g, ' ').trim();
  if (!compactText) {
    return [];
  }

  const numberedSteps = compactText
    .split(/(?:^|\s)(?=\d+\)\s*)/g)
    .map((step) => step.replace(/^\d+\)\s*/, '').trim())
    .filter(Boolean);

  if (numberedSteps.length > 1) {
    return numberedSteps;
  }

  const lineSteps = normalize(instructionsText)
    .split(/\r?\n+/)
    .map((step) => step.replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);

  if (lineSteps.length > 1) {
    return lineSteps;
  }

  return [compactText];
}

function buildNutritionItems(nutrition) {
  if (!nutrition || nutrition.vises_offentligt === false) {
    return [];
  }

  const entries = [
    { label: 'Kcal', unit: 'kcal', value: nutrition?.kcal_pr_portion },
    { label: 'Protein', unit: 'g', value: nutrition?.protein_pr_portion_g },
    { label: 'Kulhydrat', unit: 'g', value: nutrition?.kulhydrat_pr_portion_g },
    { label: 'Fedt', unit: 'g', value: nutrition?.fedt_pr_portion_g },
    { label: 'Fibre', unit: 'g', value: nutrition?.fibre_pr_portion_g },
  ];

  return entries
    .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== '')
    .map((entry) => ({
      ...entry,
      displayValue: `${Math.round(Number(entry.value))} ${entry.unit}`,
    }));
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatDkk(value) {
  const amount = toNumber(value);
  if (amount === null) {
    return '';
  }
  return `${Math.round(amount)} kr.`;
}

function formatLeftoverEntry(leftover) {
  const productName = formatIngredientLabel(leftover?.produkt_navn);
  const amount = normalize(leftover?.rest_maengde);
  const unit = normalize(leftover?.rest_enhed);
  const quantity = [amount, unit].filter(Boolean).join(' ').trim();
  if (!productName) {
    return '';
  }
  if (!quantity) {
    return `${productName} tilbage`;
  }
  return `${quantity} ${productName.toLocaleLowerCase('da-DK')} tilbage`;
}

function isEstimatedLeftover(leftover) {
  return normalize(leftover?.rest_estimated).toLowerCase() === 'ja';
}

function formatLeftoverAmount(leftover) {
  const amount = normalize(leftover?.rest_maengde);
  const unit = normalize(leftover?.rest_enhed);
  const quantity = [amount, unit].filter(Boolean).join(' ').trim();
  if (!quantity) {
    return 'Rest tilbage';
  }
  return `${isEstimatedLeftover(leftover) ? 'ca. ' : ''}${quantity} tilbage`;
}

function formatPantryQuantityLabel(amount, unit) {
  const number = toNumber(amount);
  if (number === null) {
    return 'Rest tilbage';
  }
  const formattedAmount = Number.isInteger(number)
    ? number.toString()
    : number.toLocaleString('da-DK', { maximumFractionDigits: 2 });
  return `${formattedAmount} ${normalize(unit)}`.trim() + ' tilbage';
}

function getAllocatedPackageQuantity(priceLine) {
  const packageCount = toNumber(priceLine?.pakker) || 1;
  const packageSize = toNumber(priceLine?.pakke_stoerrelse);
  const packageUnit = normalize(priceLine?.pakke_enhed);

  if (packageSize === null || !packageUnit) {
    return { amount: null, unit: '' };
  }

  return {
    amount: packageCount * packageSize,
    unit: packageUnit,
  };
}

function getPantryUsageAllocation(ingredient, pantryItem, priceLine) {
  const required = getRequiredIngredientQuantity(ingredient, priceLine);
  const available = getPantryItemQuantity(pantryItem);
  const amount = convertIngredientQuantityAmount(
    required.amount,
    required.unit,
    available.unit,
    ingredient
  );

  if (amount === null || !available.unit) {
    return getAllocatedPackageQuantity(priceLine);
  }

  return { amount, unit: available.unit };
}

function formatComponentHeading(sectionName) {
  const label = normalize(sectionName);
  if (!label) {
    return 'Til retten';
  }

  const lower = label.toLowerCase();
  if (lower.startsWith('til ')) {
    return `Til ${label.slice(4)}`;
  }
  if (lower === 'servering') {
    return 'Til servering';
  }
  return `Til ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

function getShoppingItemKey(ingredient, index) {
  return [
    normalize(ingredient.produkt_navn),
    normalize(ingredient.pakke_stoerrelse),
    normalize(ingredient.pakke_enhed),
    index
  ].join('|');
}

function getOwnedIngredientKey(productName) {
  return getPantryKey(productName);
}

function buildPriceDisplay(priceLine) {
  if (!priceLine) {
    return {
      hasPrice: false,
      isDiscounted: false,
      weekly: '',
      normal: '',
      detail: '',
      store: '',
      isReferencePrice: false,
    };
  }

  const weeklyValue = toNumber(priceLine.pris_denne_uge_dkk);
  const normalValue = toNumber(priceLine.normalpris_dkk);
  const weekly = formatDkk(priceLine.pris_denne_uge_dkk);
  const normal = formatDkk(priceLine.normalpris_dkk);
  const rawStore = normalize(priceLine.tilbud_butik);
  const pricingSource = normalize(priceLine.pricing_source).toLowerCase();
  const isReferencePrice = pricingSource === 'standardpris_fallback' || rawStore.toLowerCase() === 'standardpris';
  const referenceStore = normalize(priceLine.reference_store);
  const store = isReferencePrice
    ? referenceStore
      ? `Reference: ${referenceStore}`
      : 'Referencepris'
    : rawStore;

  if (weeklyValue !== null && normalValue !== null) {
    if (weeklyValue < normalValue) {
      return {
        hasPrice: true,
        isDiscounted: true,
        weekly,
        normal,
        detail: `${weekly} (ca. normal ${normal})`,
        store,
        isReferencePrice,
      };
    }
    return {
      hasPrice: true,
      isDiscounted: false,
      weekly,
      normal,
      detail: weekly || normal,
      store,
      isReferencePrice,
    };
  }

  if (weekly || normal) {
    return {
      hasPrice: true,
      isDiscounted: false,
      weekly,
      normal,
      detail: weekly || normal,
      store,
      isReferencePrice,
    };
  }

  return {
    hasPrice: false,
    isDiscounted: false,
    weekly: '',
    normal: '',
    detail: '',
    store,
    isReferencePrice,
  };
}

function buildPricedIngredientForMeta(ingredient, priceLine) {
  const adaptedAmount = normalize(priceLine?.adapted_brug_maengde);
  const adaptedUnit = normalize(priceLine?.adapted_brug_enhed);
  if (!adaptedAmount || !adaptedUnit) {
    return ingredient;
  }

  return {
    ...ingredient,
    brug_maengde: adaptedAmount,
    brug_enhed: adaptedUnit,
    brug_er_hele_pakken: '',
  };
}

function getIngredientDisplayName(ingredient, priceLine) {
  return formatIngredientLabel(
    priceLine?.selected_product_name ||
    ingredient?.produkt_navn ||
    priceLine?.ingredient_family_name ||
    ingredient?.ingredient_family_name
  );
}

function buildShoppingMetaWithPriceFallback(ingredient, priceLine) {
  const pricedIngredient = buildPricedIngredientForMeta(ingredient, priceLine);
  const usage = formatUsage(pricedIngredient);
  const rawIngredientPackage = formatPackageSize(ingredient);
  const fallbackPackage = [normalize(priceLine?.pakke_stoerrelse), normalize(priceLine?.pakke_enhed)]
    .filter(Boolean)
    .join(' ')
    .trim();
  const usageUnit = normalize(ingredient?.brug_enhed).toLowerCase();
  const ingredientPackageUnit = normalize(ingredient?.pakke_enhed).toLowerCase();
  const fallbackPackageUnit = normalize(priceLine?.pakke_enhed).toLowerCase();
  const explicitPackageCount = normalize(ingredient?.antal_pakker);
  const purchasedPackages = normalize(priceLine?.pakker);
  const shouldIgnoreIngredientPackage =
    usageUnit === 'stk' &&
    !ingredientPackageUnit &&
    explicitPackageCount &&
    rawIngredientPackage === explicitPackageCount &&
    ['g', 'kg', 'ml', 'l'].includes(fallbackPackageUnit);

  const ingredientPackage = shouldIgnoreIngredientPackage ? '' : rawIngredientPackage;
  const fallbackPurchaseMeta = fallbackPackage
    ? `${purchasedPackages && purchasedPackages !== '1' ? `${purchasedPackages} pakker á` : '1 pakke á'} ${fallbackPackage}`
    : '';
  const shouldPreferFallbackPurchaseMeta =
    Boolean(fallbackPurchaseMeta) &&
    (
      !ingredientPackage ||
      (ingredientPackageUnit &&
        fallbackPackageUnit &&
        ingredientPackageUnit === fallbackPackageUnit &&
        normalize(ingredient?.pakke_stoerrelse) !== normalize(priceLine?.pakke_stoerrelse)) ||
      (explicitPackageCount && purchasedPackages && explicitPackageCount !== purchasedPackages)
    );
  const packageSize = shouldPreferFallbackPurchaseMeta
    ? fallbackPurchaseMeta
    : ingredientPackage || fallbackPurchaseMeta;

  if (usage && packageSize) {
    if (usage === 'Hele pakken') {
      return `Hele pakken (${packageSize})`;
    }
    if (
      usageUnit === 'stk' &&
      !ingredientPackageUnit &&
      ['g', 'kg', 'ml', 'l'].includes(fallbackPackageUnit)
    ) {
      return `${usage} (fra ${packageSize})`;
    }
    if (
      shouldPreferFallbackPurchaseMeta &&
      ['g', 'kg', 'ml', 'l', 'stk'].includes(fallbackPackageUnit)
    ) {
      return `${usage} (købes som ${packageSize})`;
    }
    return `${usage} (${packageSize})`;
  }
  return usage || packageSize;
}

function formatBasisMeta(ingredient) {
  const note = normalize(ingredient?.note);
  const lowerNote = note.toLowerCase();
  const name = normalize(ingredient?.produkt_navn).toLowerCase();

  if (!note || lowerNote === 'fra krydreskab') {
    if (name === 'salt' || name === 'peber') {
      return 'efter smag';
    }
    return '';
  }

  return note;
}

function toBool(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['ja', 'yes', 'true', '1'].includes(normalized)) {
      return true;
    }
    if (['nej', 'no', 'false', '0'].includes(normalized)) {
      return false;
    }
  }
  return fallback;
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  return copied;
}

function RecipeView() {
  const { id } = useParams();
  const location = useLocation();
  const shoppingStorageKey = `recipe-shopping-${normalize(id)}`;
  const ownedIngredientsStorageKey = PANTRY_STORAGE_KEY;
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCookingMode, setIsCookingMode] = useState(false);
  const [isFlavorTextExpanded, setIsFlavorTextExpanded] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({});
  const [checkedShoppingItems, setCheckedShoppingItems] = useState({});
  const [ownedIngredients, setOwnedIngredients] = useState(() => loadPantryState());
  const [pantryUsageConfirmed, setPantryUsageConfirmed] = useState(false);
  const [smartPantryUpdated, setSmartPantryUpdated] = useState(false);
  const [pantryFlowStatus, setPantryFlowStatus] = useState('');
  const [recipeActionStatus, setRecipeActionStatus] = useState('');
  const [wakeLockMessage, setWakeLockMessage] = useState('');
  const wakeLockRef = useRef(null);
  const shoppingSectionRef = useRef(null);
  const canUseWakeLock = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  const inventoryMode = useMemo(() => loadProfile().inventoryMode, []);

  const releaseWakeLock = async () => {
    if (!wakeLockRef.current) {
      return;
    }
    try {
      await wakeLockRef.current.release();
    } catch {
      // Ignore release failures and just clear the local reference.
    } finally {
      wakeLockRef.current = null;
    }
  };

  const requestWakeLock = async () => {
    if (!canUseWakeLock || typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return false;
    }

    try {
      const sentinel = await navigator.wakeLock.request('screen');
      wakeLockRef.current = sentinel;
      setWakeLockMessage('Skærmen holdes vågen, mens madlavningstilstand er aktiv.');

      sentinel.addEventListener('release', () => {
        if (wakeLockRef.current === sentinel) {
          wakeLockRef.current = null;
        }
        if (document.visibilityState === 'visible') {
          setWakeLockMessage('Skærmen kan stadig slukke på denne enhed, hvis browseren ikke tillader wake lock.');
        }
      });

      return true;
    } catch {
      setWakeLockMessage('Skærmen kan stadig slukke på denne enhed, hvis browseren ikke tillader wake lock.');
      return false;
    }
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    const loadRecipe = async () => {
      try {
        const json = await fetchJsonOrThrow(`${api_url}api/recipes/${encodeURIComponent(normalize(id))}`);

        const hasMatchingMealPlanPricing = normalize(location.state?.mealPlanRecipeId).toLowerCase()
          === normalize(id).toLowerCase()
          && location.state?.mealPlanPublicPrice;
        setRecipe(hasMatchingMealPlanPricing
          ? { ...json, public_price: location.state.mealPlanPublicPrice }
          : json || null);
      } catch (fetchError) {
        setError('Opskriften kunne ikke hentes lige nu.');
      } finally {
        setLoading(false);
      }
    };

    loadRecipe().catch(() => {
      setLoading(false);
      setError('Kunne ikke hente opskriften.');
    });
  }, [id, location.state]);

  useEffect(() => {
    setCompletedSteps({});
    setIsCookingMode(false);
    setIsFlavorTextExpanded(false);
    setWakeLockMessage('');
    setPantryUsageConfirmed(false);
    setSmartPantryUpdated(false);
    setPantryFlowStatus('');
    setRecipeActionStatus('');
  }, [id]);

  useEffect(() => () => {
    releaseWakeLock().catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const savedValue = window.localStorage.getItem(shoppingStorageKey);
      if (!savedValue) {
        setCheckedShoppingItems({});
        return;
      }

      const parsedValue = JSON.parse(savedValue);
      setCheckedShoppingItems(parsedValue && typeof parsedValue === 'object' ? parsedValue : {});
    } catch {
      setCheckedShoppingItems({});
    }
  }, [shoppingStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(shoppingStorageKey, JSON.stringify(checkedShoppingItems));
    } catch {
      // Ignore storage errors and keep the checklist usable in-memory.
    }
  }, [shoppingStorageKey, checkedShoppingItems]);

  useEffect(() => {
    if (!savePantryState(ownedIngredients)) {
      // Ignore storage errors and keep local ownership usable in-memory.
    }
  }, [ownedIngredientsStorageKey, ownedIngredients]);

  useEffect(() => {
    if (!isCookingMode) {
      setWakeLockMessage('');
      releaseWakeLock().catch(() => {});
      return;
    }

    if (!canUseWakeLock) {
      setWakeLockMessage('Skærmen kan stadig slukke på denne enhed, fordi wake lock ikke understøttes i browseren.');
      return;
    }

    requestWakeLock().catch(() => {});

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isCookingMode && !wakeLockRef.current) {
        requestWakeLock().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock().catch(() => {});
    };
  }, [isCookingMode]);

  const sections = useMemo(() => {
    if (!recipe) {
      return null;
    }

    const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
    const nonVariationIngredients = ingredients.filter(
      (ingredient) => normalizeSection(ingredient.sektion) !== 'variation'
    );
    const requiredTilKurv = nonVariationIngredients.filter((ingredient) =>
      toBool(ingredient.in_shopping_basket, toBool(ingredient.required_in_base) && !toBool(ingredient.pantry_basic))
    );
    const basis = nonVariationIngredients.filter(
      (ingredient) =>
        toBool(ingredient.required_in_base, true) &&
        toBool(ingredient.pantry_basic, normalizeSection(ingredient.sektion) === 'basis')
    );
    const ekstraOptional = nonVariationIngredients.filter(
      (ingredient) => !toBool(ingredient.required_in_base, true)
    );
    const cookingIngredients = nonVariationIngredients.filter((ingredient) =>
      toBool(ingredient.required_in_base, true)
    );
    const rawCookingGroups = cookingIngredients.reduce((acc, ingredient) => {
      const sectionName = normalizeSection(ingredient.sektion);
      const groupKey = sectionName && sectionName !== 'basis' ? sectionName : 'samlet';
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(ingredient);
      return acc;
    }, {});
    const cookingGroupEntries = Object.entries(rawCookingGroups);
    const hasComponentBasedCookingGroups =
      cookingGroupEntries.length > 1 && cookingGroupEntries.some(([groupName]) => groupName !== 'samlet');
    const cookingGroups = hasComponentBasedCookingGroups
      ? cookingGroupEntries.map(([groupName, rows]) => ({
        key: groupName,
        title: formatComponentHeading(groupName),
        items: rows,
      }))
      : [{
        key: 'samlet',
        title: 'Til retten',
        items: cookingIngredients,
      }];

    return {
      ekstraOptional,
      requiredTilKurv,
      brugIRetten: requiredTilKurv.filter(
        (ingredient) =>
          normalize(ingredient.brug_maengde) ||
          normalize(ingredient.brug_enhed) ||
          normalize(ingredient.brug_er_hele_pakken)
      ),
      basis,
      cookingGroups
    };
  }, [recipe]);

  if (loading) {
    return (
      <Container className="recipe-view-page editorial-page">
        <section className="recipe-state-card is-loading" aria-live="polite">
          <div className="recipe-state-icon" aria-hidden="true">...</div>
          <div>
            <h2>Henter opskriften</h2>
            <p>Vi samler ingredienser, pris og trin, så siden er klar til madlavning.</p>
          </div>
        </section>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="recipe-view-page editorial-page">
        <section className="recipe-state-card is-error" role="status">
          <div className="recipe-state-icon" aria-hidden="true">!</div>
          <div className="recipe-state-copy">
            <h2>Opskriften er ikke tilgængelig</h2>
            <p>{error} Prøv igen om lidt, eller gå tilbage til opskriftsoversigten.</p>
            <div className="recipe-state-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={() => window.location.reload()}>
                Prøv igen
              </button>
              <Link to="/recipes" className="recipe-state-link">Se opskrifter</Link>
            </div>
          </div>
        </section>
      </Container>
    );
  }

  if (!recipe || !sections) {
    return (
      <Container className="recipe-view-page editorial-page">
        <section className="recipe-state-card">
          <div className="recipe-state-icon" aria-hidden="true">?</div>
          <div className="recipe-state-copy">
            <h2>Opskriften blev ikke fundet</h2>
            <p>Den kan være fjernet, eller linket kan være ændret.</p>
            <div className="recipe-state-actions">
              <Link to="/recipes" className="recipe-state-link">Tilbage til opskrifter</Link>
            </div>
          </div>
        </section>
      </Container>
    );
  }

  const { ekstraOptional, requiredTilKurv, brugIRetten, basis } = sections;
  const instructionSteps = parseInstructionSteps(recipe.instructions);
  const hasInstructions = instructionSteps.length > 0;
  const completedStepCount = instructionSteps.filter((_, index) => completedSteps[index]).length;
  const cookingProgressPercentage = instructionSteps.length > 0
    ? Math.round((completedStepCount / instructionSteps.length) * 100)
    : 0;
  const requiredGear = normalize(recipe.gear_required);
  const optionalGear = normalize(recipe.gear_optional);
  const hasRequiredGear = requiredGear && requiredGear.toLowerCase() !== 'na';
  const hasOptionalGear = optionalGear && optionalGear.toLowerCase() !== 'na';
  const timeRange = formatTimeRange(recipe.tid_min, recipe.tid_max);
  const requiredGearItems = hasRequiredGear ? splitGearItems(requiredGear) : [];
  const optionalGearItems = hasOptionalGear ? splitGearItems(optionalGear) : [];
  const nutritionItems = buildNutritionItems(recipe.nutrition);
  const publicPrice = recipe.public_price || {};
  const lineItems = Array.isArray(publicPrice.line_items) ? publicPrice.line_items : [];
  const priceLineByProduct = new Map(
    lineItems
      .map((line) => [normalize(line.produkt_navn).toLowerCase(), line])
      .filter(([key]) => key)
  );
  const requiredProductKeys = new Set(
    requiredTilKurv
      .map((ingredient) => normalize(ingredient.produkt_navn).toLowerCase())
      .filter(Boolean)
  );
  const requiredPriceLines = lineItems.filter((line) =>
    requiredProductKeys.has(normalize(line.produkt_navn).toLowerCase())
  );
  const requiredWeekTotal = requiredPriceLines.reduce((sum, line) => {
    const value = toNumber(line.pris_denne_uge_dkk);
    return value === null ? sum : sum + value;
  }, 0);
  const requiredNormalTotal = requiredPriceLines.reduce((sum, line) => {
    const value = toNumber(line.normalpris_dkk);
    return value === null ? sum : sum + value;
  }, 0);
  const hasRequiredPriceLines = requiredPriceLines.length > 0;
  const weeklyPrice = formatDkk(hasRequiredPriceLines ? requiredWeekTotal : publicPrice.pris_denne_uge_dkk);
  const computedSavings = hasRequiredPriceLines
    ? Math.max(requiredNormalTotal - requiredWeekTotal, 0)
    : publicPrice.besparelse_dkk;
  const savingsPrice = formatDkk(computedSavings);
  const leftoverRows = Array.isArray(publicPrice.leftovers)
    ? publicPrice.leftovers.map((leftover, index) => {
      const sourceLine = priceLineByProduct.get(normalize(leftover?.produkt_navn).toLowerCase());
      const productName = formatIngredientLabel(
        leftover?.selected_product_name ||
        sourceLine?.selected_product_name ||
        leftover?.produkt_navn
      );
      const amountLabel = formatLeftoverAmount(leftover);
      const fullLabel = formatLeftoverEntry({
        ...leftover,
        produkt_navn: productName,
      });
      const estimateNote = normalize(leftover?.rest_note);

      return {
        id: `${getOwnedIngredientKey(productName)}-${index}`,
        productKey: getOwnedIngredientKey(productName),
        sourceProductKey: getOwnedIngredientKey(leftover?.produkt_navn),
        label: fullLabel,
        amountLabel,
        amount: toNumber(leftover?.rest_maengde),
        unit: normalize(leftover?.rest_enhed),
        productName,
        isEstimated: isEstimatedLeftover(leftover),
        estimateNote,
      };
    }).filter((leftover) => leftover.productKey && leftover.productName)
    : [];
  const pantryItems = pantryStateToItems(ownedIngredients);
  const matchedPantryItems = pantryItems.filter((pantryItem) =>
    requiredTilKurv.some((ingredient) => pantryItemCoversIngredient(
      ingredient,
      pantryItem,
      priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase())
    ))
  );
  const isLeftoverSaved = (leftover) => {
    const entry = ownedIngredients[leftover.productKey];
    return Boolean(
      entry?.owned
      && entry.source === 'leftover'
      && (!entry.sourceRecipeId || entry.sourceRecipeId === recipe.recipe_id)
    );
  };
  const savedLeftoverCount = leftoverRows.filter(isLeftoverSaved).length;
  const requiresPantryUsageConfirmation = matchedPantryItems.length > 0 && !pantryUsageConfirmed;
  const areAllLeftoversSaved = leftoverRows.length > 0 && savedLeftoverCount === leftoverRows.length;
  const hasPriceData = Boolean(weeklyPrice);
  const heroImageCandidates = getRecipeImageCandidates(recipe);
  const hasHeroImage = heroImageCandidates.length > 0;
  const shoppingPricingRows = requiredTilKurv.map((ingredient, index) => {
    const itemKey = getShoppingItemKey(ingredient, index);
    const matchedPantryItem = matchedPantryItems.find((pantryItem) =>
      pantryItemCoversIngredient(
        ingredient,
        pantryItem,
        priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase())
      )
    );
    const ownedKey = matchedPantryItem?.key || getOwnedIngredientKey(ingredient.produkt_navn);
    const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
    return {
      ingredient,
      itemKey,
      ownedKey,
      priceLine,
      weeklyValue: toNumber(priceLine?.pris_denne_uge_dkk),
      normalValue: toNumber(priceLine?.normalpris_dkk),
      isChecked: Boolean(checkedShoppingItems[itemKey]),
      isOwned: Boolean(matchedPantryItem?.owned),
    };
  });
  const isLoadBearingIngredient = (ingredient) => {
    const role = normalize(ingredient.anbefalingsrolle).toLowerCase();
    const isCoreDriver = normalize(ingredient.er_kernedriver).toLowerCase();
    return isCoreDriver === 'ja' || role === 'kerne' || role === 'baerende';
  };
  const pricedShoppingLinesCount = shoppingPricingRows.filter(({ priceLine }) =>
    buildPriceDisplay(priceLine).hasPrice
  ).length;
  const missingShoppingLinesCount = requiredTilKurv.length - pricedShoppingLinesCount;
  const fullWeekTotalValue = hasRequiredPriceLines
    ? requiredWeekTotal
    : toNumber(publicPrice.pris_denne_uge_dkk);
  const fullNormalTotalValue = hasRequiredPriceLines
    ? requiredNormalTotal
    : toNumber(publicPrice.normalpris_dkk);
  const hasAnyDynamicWeeklyPricing = shoppingPricingRows.some(({ weeklyValue }) => weeklyValue !== null);
  const hasAnyDynamicNormalPricing = shoppingPricingRows.some(({ normalValue }) => normalValue !== null);
  const hasCompleteDynamicWeeklyPricing =
    requiredTilKurv.length > 0 &&
    shoppingPricingRows.every(({ weeklyValue }) => weeklyValue !== null);
  const hasCompleteDynamicPricing =
    requiredTilKurv.length > 0 &&
    shoppingPricingRows.every(({ weeklyValue, normalValue }) => weeklyValue !== null && normalValue !== null);
  const checkedShoppingCount = shoppingPricingRows.filter(({ isChecked, isOwned }) => isChecked || isOwned).length;
  const ownedShoppingCount = shoppingPricingRows.filter(({ isOwned }) => isOwned).length;
  const remainingShoppingCount = Math.max(requiredTilKurv.length - checkedShoppingCount, 0);
  const displayedPriceLabel = hasCompleteDynamicWeeklyPricing
    ? ownedShoppingCount > 0
      ? 'Med dit spisekammer'
      : checkedShoppingCount > 0
        ? 'Pris efter dine valg'
        : 'Pris denne uge'
    : missingShoppingLinesCount > 0
      ? hasAnyDynamicWeeklyPricing && ownedShoppingCount > 0 ? 'Foreløbig pris med spisekammer' : 'Foreløbig pris'
      : 'Pris denne uge';
  const dynamicWeekTotalValue = hasAnyDynamicWeeklyPricing
    ? shoppingPricingRows.reduce((sum, row) => (
      row.isChecked || row.isOwned || row.weeklyValue === null ? sum : sum + row.weeklyValue
    ), 0)
    : fullWeekTotalValue;
  const dynamicNormalTotalValue = hasAnyDynamicNormalPricing
    ? shoppingPricingRows.reduce((sum, row) => (
      row.isChecked || row.isOwned || row.normalValue === null ? sum : sum + row.normalValue
    ), 0)
    : fullNormalTotalValue;
  const dynamicSavingsValue =
    dynamicWeekTotalValue !== null && dynamicNormalTotalValue !== null
      ? Math.max(dynamicNormalTotalValue - dynamicWeekTotalValue, 0)
      : null;
  const dynamicWeeklyPrice = formatDkk(dynamicWeekTotalValue);
  const dynamicNormalPrice = formatDkk(dynamicNormalTotalValue);
  const displayedWeeklyPrice = hasAnyDynamicWeeklyPricing ? dynamicWeeklyPrice : weeklyPrice;
  const fullBasketWeeklyPrice = formatDkk(fullWeekTotalValue);
  const fullBasketNormalPrice = formatDkk(fullNormalTotalValue);
  const dynamicSavingsPrice = formatDkk(dynamicSavingsValue);
  const mergedPriceSummary = hasCompleteDynamicPricing
    ? [
      fullBasketWeeklyPrice
        ? `${ownedShoppingCount > 0 ? 'Uden spisekammer' : 'Fuld kurv'} ${fullBasketWeeklyPrice}`
        : '',
      fullBasketNormalPrice ? `Ca. normalpris ${fullBasketNormalPrice}` : '',
      dynamicSavingsPrice ? `Ugens besparelse ca. ${dynamicSavingsPrice}` : '',
    ].filter(Boolean)
    : [];
  const buildInlineShoppingMeta = (ingredient) => {
    const details = [];
    const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
    const itemMeta = buildShoppingMetaWithPriceFallback(ingredient, priceLine);
    if (itemMeta) {
      details.push(itemMeta);
    }
    const priceDisplay = buildPriceDisplay(priceLine);
    if (priceDisplay.detail) {
      details.push(priceDisplay.detail);
    }
    if (priceDisplay.store) {
      details.push(priceDisplay.isReferencePrice ? priceDisplay.store : `Køb hos ${priceDisplay.store}`);
    }
    return details.join(' • ');
  };
  const visibleShoppingList =
    isCookingMode && brugIRetten.length > 0 ? brugIRetten : requiredTilKurv;
  const shoppingDisplayItems = [
    ...requiredTilKurv.map((ingredient, index) => ({
      ingredient,
      index,
      itemKey: getShoppingItemKey(ingredient, index),
      isOptional: false,
    })),
    ...ekstraOptional.map((ingredient, index) => ({
      ingredient,
      index,
      itemKey: `optional|${getShoppingItemKey(ingredient, index)}`,
      isOptional: true,
    })),
  ];
  const shoppingCountLabel = ekstraOptional.length > 0
    ? requiredTilKurv.length > 0
      ? `${requiredTilKurv.length} varer + ${ekstraOptional.length} tilvalg`
      : `${ekstraOptional.length} tilvalg`
    : `${requiredTilKurv.length} varer`;
  const featuredOfferNames = requiredTilKurv
    .slice(0, 3)
    .map((ingredient) => {
      const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
      return getIngredientDisplayName(ingredient, priceLine);
    });
  const flavorText = normalize(recipe.flavor_text);
  const shouldOfferFlavorTextExpansion = flavorText.length > 140;
  const heroImageSrc = heroImageCandidates[0] || PLACEHOLDER_IMAGE;
  const heroImagePosition = getRecipeImagePosition(recipe.recipe_id);

  const buildRecipeShoppingListText = () => {
    const remainingRows = shoppingPricingRows.filter(({ isChecked, isOwned }) => !isChecked && !isOwned);
    const requiredLines = remainingRows.map(({ ingredient, priceLine }) => {
      const name = getIngredientDisplayName(ingredient, priceLine);
      const meta = buildShoppingMetaWithPriceFallback(ingredient, priceLine);
      return `- ${name}${meta ? ` — ${meta}` : ''}`;
    });
    const optionalLines = ekstraOptional.map((ingredient) => {
      const note = formatOptionalIngredientNote(ingredient);
      return `- ${formatIngredientLabel(ingredient.produkt_navn)}${note ? ` — ${note}` : ''} (valgfri)`;
    });
    const url = typeof window !== 'undefined' ? window.location.href : '';

    return [
      `Indkøbsliste til ${recipe.name}`,
      '',
      requiredLines.length > 0 ? requiredLines.join('\n') : 'Du har allerede alle nødvendige indkøbsvarer markeret.',
      optionalLines.length > 0 ? `\nValgfrit:\n${optionalLines.join('\n')}` : '',
      url ? `\nOpskrift: ${url}` : '',
    ].filter(Boolean).join('\n');
  };

  const handleOpenRecipeShoppingList = () => {
    setRecipeActionStatus('');
    shoppingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleShareRecipeShoppingList = async () => {
    const text = buildRecipeShoppingListText();
    setRecipeActionStatus('');

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Indkøbsliste til ${recipe.name}`,
          text,
          url: window.location.href,
        });
        setRecipeActionStatus('Indkøbslisten er delt.');
        return;
      }

      const copied = await copyTextToClipboard(text);
      setRecipeActionStatus(copied
        ? 'Indkøbslisten er kopieret og klar til at sende.'
        : 'Indkøbslisten kunne ikke kopieres automatisk.');
    } catch (shareError) {
      if (shareError?.name !== 'AbortError') {
        setRecipeActionStatus('Indkøbslisten kunne ikke deles lige nu.');
      }
    }
  };

  const handlePrintRecipe = () => {
    setRecipeActionStatus('');
    window.print();
  };

  const toggleStep = (index) => {
    setCompletedSteps((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleShoppingItem = (itemKey, ownedKey = '') => {
    const matchingRow = shoppingPricingRows.find((row) => row.itemKey === itemKey);
    const matchingOwnedKey = matchingRow?.ownedKey || ownedKey;
    if (matchingOwnedKey && matchingRow?.isOwned) {
      setOwnedIngredients((prev) => {
        const next = { ...prev };
        delete next[matchingOwnedKey];
        return next;
      });
      return;
    }

    setCheckedShoppingItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const handleConfirmPantryUsage = () => {
    if (matchedPantryItems.length === 0) {
      return;
    }

    const timestamp = new Date().toISOString();
    setOwnedIngredients((previousItems) => {
      const nextItems = { ...previousItems };

      matchedPantryItems.forEach((pantryItem) => {
        const ingredient = requiredTilKurv.find((candidate) =>
          pantryItemCoversIngredient(
            candidate,
            pantryItem,
            priceLineByProduct.get(normalize(candidate.produkt_navn).toLowerCase())
          )
        );
        const priceLine = priceLineByProduct.get(normalize(ingredient?.produkt_navn).toLowerCase());
        const allocation = getAllocatedPackageQuantity(priceLine);
        const currentEntry = nextItems[pantryItem.key];
        const remainingEntry = consumePantryEntry(currentEntry, allocation, timestamp);

        if (!remainingEntry) {
          delete nextItems[pantryItem.key];
          return;
        }

        nextItems[pantryItem.key] = remainingEntry;
      });

      return nextItems;
    });
    setPantryUsageConfirmed(true);
    setPantryFlowStatus(
      `${matchedPantryItems.length} ${matchedPantryItems.length === 1 ? 'vare er' : 'varer er'} registreret som brugt. Gem nu det, du har tilbage.`
    );
  };

  const addLeftoverToPantryState = (state, leftover, trackingMode = 'precise') => {
    const existingEntry = state[leftover.productKey];
    const existingAmount = toNumber(existingEntry?.quantityAmount);
    const hasCompatibleQuantity = existingAmount !== null
      && leftover.amount !== null
      && normalize(existingEntry?.quantityUnit).toLowerCase() === leftover.unit.toLowerCase();
    const combinedAmount = hasCompatibleQuantity
      ? existingAmount + leftover.amount
      : leftover.amount;
    const amountLabel = combinedAmount !== null
      ? formatPantryQuantityLabel(combinedAmount, leftover.unit)
      : leftover.amountLabel;

    return {
      ...state,
      [leftover.productKey]: createPantryEntry({
        productName: leftover.productName,
        amountLabel,
        source: 'leftover',
        note: leftover.estimateNote,
        category: existingEntry?.category,
        freshnessKind: 'short',
        quantityAmount: combinedAmount,
        quantityUnit: leftover.unit,
        sourceRecipeId: recipe.recipe_id,
        trackingMode,
        confidence: trackingMode === 'smart' ? 'estimated' : '',
      }),
    };
  };

  const handleSmartPantryUpdate = () => {
    if (smartPantryUpdated) {
      return;
    }

    const timestamp = new Date().toISOString();
    setOwnedIngredients((previousItems) => {
      let nextItems = { ...previousItems };

      matchedPantryItems.forEach((pantryItem) => {
        const ingredient = requiredTilKurv.find((candidate) =>
          pantryItemCoversIngredient(
            candidate,
            pantryItem,
            priceLineByProduct.get(normalize(candidate.produkt_navn).toLowerCase())
          )
        );
        const priceLine = priceLineByProduct.get(normalize(ingredient?.produkt_navn).toLowerCase());
        const remainingEntry = consumePantryEntry(
          nextItems[pantryItem.key],
          getPantryUsageAllocation(ingredient, pantryItem, priceLine),
          timestamp
        );

        if (remainingEntry) {
          nextItems[pantryItem.key] = {
            ...remainingEntry,
            source: 'leftover',
            sourceRecipeId: recipe.recipe_id,
            trackingMode: 'smart',
            confidence: 'estimated',
          };
        } else {
          delete nextItems[pantryItem.key];
        }
      });

      leftoverRows.forEach((leftover) => {
        const existingEntry = nextItems[leftover.productKey];
        const isAlreadySavedForRecipe = existingEntry?.source === 'leftover'
          && existingEntry?.sourceRecipeId === recipe.recipe_id;

        if (!isAlreadySavedForRecipe) {
          nextItems = addLeftoverToPantryState(nextItems, leftover, 'smart');
        }
      });

      return nextItems;
    });
    setPantryUsageConfirmed(true);
    setSmartPantryUpdated(true);
    setCompletedSteps(Object.fromEntries(instructionSteps.map((_, index) => [index, true])));
    setPantryFlowStatus(
      `Smart spisekammer har beregnet forbruget og registreret ${leftoverRows.length} ${leftoverRows.length === 1 ? 'sandsynlig rest' : 'sandsynlige rester'}.`
    );
  };

  const handleSaveLeftover = (leftover) => {
    if (requiresPantryUsageConfirmation || isLeftoverSaved(leftover)) {
      return;
    }

    setOwnedIngredients((previousItems) => addLeftoverToPantryState(previousItems, leftover));
    setPantryFlowStatus(`${leftover.productName} er gemt i dit spisekammer.`);
  };

  const handleSaveAllLeftovers = () => {
    if (requiresPantryUsageConfirmation || leftoverRows.length === 0 || areAllLeftoversSaved) {
      return;
    }

    setOwnedIngredients((previousItems) => leftoverRows.reduce(
      (nextItems, leftover) => isLeftoverSaved(leftover)
        ? nextItems
        : addLeftoverToPantryState(nextItems, leftover),
      previousItems
    ));
    setPantryFlowStatus(
      `${leftoverRows.length} ${leftoverRows.length === 1 ? 'rest er' : 'rester er'} gemt i dit spisekammer.`
    );
  };

  const renderPriorityList = (items, getMeta, className = 'recipe-clean-list', useLayeredName = false) => (
    <ul className={className}>
      {items.map((ingredient, index) => {
        const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
        const meta = getMeta(ingredient);
        return (
          <li key={`${ingredient.produkt_navn}-${index}`}>
            <strong>{useLayeredName ? getIngredientDisplayName(ingredient, priceLine) : formatIngredientLabel(ingredient.produkt_navn)}</strong>
            {meta ? <span>{meta}</span> : null}
          </li>
        );
      })}
    </ul>
  );

  const renderFlavorText = () => {
    if (!flavorText) {
      return null;
    }

    return (
      <div className="recipe-flavor-text-block">
        <p className={`recipe-flavor-text ${isFlavorTextExpanded ? 'is-expanded' : ''}`.trim()}>
          {flavorText}
        </p>
        {shouldOfferFlavorTextExpansion && (
          <button
            type="button"
            className="recipe-flavor-text-toggle"
            onClick={() => setIsFlavorTextExpanded((prev) => !prev)}
            aria-expanded={isFlavorTextExpanded}
            aria-label={isFlavorTextExpanded ? 'Fold smagsbeskrivelsen sammen' : 'Udvid smagsbeskrivelsen'}
          >
            {isFlavorTextExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>
    );
  };

  const renderPriceTab = (priceDisplay, isLoadBearing) => {
    if (!priceDisplay.hasPrice) {
      return null;
    }

    const toneClass = priceDisplay.isDiscounted
      ? isLoadBearing
        ? 'is-loadbearing'
        : 'is-discount'
      : 'is-neutral';

    return (
      <div className={`recipe-shopping-price-tab ${toneClass}`}>
        {priceDisplay.isDiscounted ? (
          <small>Tilbud</small>
        ) : (
          <small>Pris</small>
        )}
        <strong>{priceDisplay.weekly || priceDisplay.normal}</strong>
        {priceDisplay.isDiscounted && priceDisplay.normal ? (
          <span>ca. normal {priceDisplay.normal}</span>
        ) : null}
        {priceDisplay.store ? <em>{priceDisplay.store}</em> : null}
      </div>
    );
  };

  const renderShoppingChecklist = (items) => (
    <ul className="recipe-clean-list recipe-clean-list-compact recipe-shopping-checklist">
      {items.map(({ ingredient, itemKey, isOptional }) => {
        const matchingPricingRow = shoppingPricingRows.find((row) => row.itemKey === itemKey);
        const ownedKey = matchingPricingRow?.ownedKey || getOwnedIngredientKey(ingredient.produkt_navn);
        const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
        const itemMeta = isOptional
          ? formatOptionalIngredientNote(ingredient)
          : buildShoppingMetaWithPriceFallback(ingredient, priceLine);
        const priceDisplay = buildPriceDisplay(priceLine);
        const isLoadBearing = isLoadBearingIngredient(ingredient);
        const isOwned = Boolean(matchingPricingRow?.isOwned);
        const isChecked = Boolean(checkedShoppingItems[itemKey]) || isOwned;
        const lineClasses = [
          'recipe-shopping-row',
          isOptional ? 'is-optional' : '',
          isChecked ? 'is-checked' : '',
          isOwned ? 'is-owned' : ''
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li key={itemKey} className={lineClasses}>
            <label className="recipe-shopping-item-label">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleShoppingItem(itemKey, ownedKey)}
                aria-label={
                  isOwned
                    ? `Fjern ${formatIngredientLabel(ingredient.produkt_navn)} fra dine gemte hjemmevarer`
                    : `Marker ${formatIngredientLabel(ingredient.produkt_navn)} som hjemme eller allerede købt`
                }
              />
              <div className="recipe-shopping-copy">
                <span className="recipe-shopping-name-line">
                  <span className="recipe-shopping-item-name">
                    {getIngredientDisplayName(ingredient, priceLine)}
                  </span>
                  {isOptional ? <span className="recipe-shopping-optional-badge">Valgfri</span> : null}
                  {isOwned ? (
                    <span
                      className="recipe-shopping-pantry-badge"
                      title="Markeret automatisk, fordi varen findes i Dit spisekammer"
                    >
                      <HouseDoorFill size={12} aria-hidden="true" />
                      <span>Dit spisekammer</span>
                    </span>
                  ) : null}
                </span>
                {itemMeta ? <span className="recipe-shopping-item-usage">{itemMeta}</span> : null}
              </div>
            </label>
            {isOptional ? (
              <div className="recipe-shopping-optional-status">
                <small>Tilvalg</small>
                <span>Ikke med i pris</span>
              </div>
            ) : renderPriceTab(priceDisplay, isLoadBearing)}
          </li>
        );
      })}
    </ul>
  );

  const renderLeftoverOwnershipList = (readOnly = false) => {
    if (leftoverRows.length === 0) {
      return <p className="recipe-nutrition-empty">Ingen registrerede rester for denne ret.</p>;
    }

    return (
      <ul className="recipe-leftover-list">
        {leftoverRows.map((leftover) => {
          const isOwned = isLeftoverSaved(leftover);
          const saveLabel = isOwned ? 'Gemt' : 'Gem rest';

          return (
            <li
              key={leftover.id}
              className={['recipe-leftover-row', isOwned ? 'is-saved' : ''].filter(Boolean).join(' ')}
            >
              <div className="recipe-leftover-copy">
                <span className="recipe-leftover-kicker">
                  {leftover.isEstimated ? 'Sandsynlig rest' : 'Beregnet rest'}
                </span>
                <strong>{leftover.productName}</strong>
                <span>{leftover.amountLabel}</span>
                {leftover.estimateNote ? <em>{leftover.estimateNote}</em> : null}
              </div>
              {readOnly ? (
                <span className={`recipe-leftover-smart-status${isOwned ? ' is-saved' : ''}`}>
                  {isOwned ? <Check2Circle size={16} aria-hidden="true" /> : <LightningCharge size={16} aria-hidden="true" />}
                  {isOwned ? 'Registreret' : 'Klar til auto'}
                </span>
              ) : (
                <button
                  type="button"
                  className="recipe-leftover-save-button"
                  aria-pressed={isOwned}
                  onClick={() => handleSaveLeftover(leftover)}
                  disabled={requiresPantryUsageConfirmation || isOwned}
                  aria-label={`${saveLabel}: ${leftover.productName}`}
                >
                  {isOwned ? <Check2Circle size={17} aria-hidden="true" /> : <PlusCircle size={17} aria-hidden="true" />}
                  <span>{saveLabel}</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderPrioritySection = (className = '') => (
    <section
      ref={shoppingSectionRef}
      id="recipe-shopping-list"
      className={`recipe-detail-section recipe-shopping-section ${className}`.trim()}
    >
      <div className="recipe-section-header">
        <h3>Indkøb og pris</h3>
        <p>Køb det du mangler, og marker det du allerede har hjemme.</p>
        {missingShoppingLinesCount > 0 && (
          <p className="recipe-shopping-note">
            {missingShoppingLinesCount} ingrediens{missingShoppingLinesCount === 1 ? '' : 'er'} mangler endnu prisdata.
          </p>
        )}
      </div>
      {hasPriceData ? (
        <div className="recipe-shopping-overview">
          <div className="recipe-shopping-overview-price">
            <small>{displayedPriceLabel}</small>
            <strong>{displayedWeeklyPrice}</strong>
          </div>
          <div className="recipe-shopping-overview-meta">
            <span className="recipe-overview-chip">
              Du mangler {remainingShoppingCount} af {requiredTilKurv.length} varer
            </span>
            {ownedShoppingCount > 0 && (
              <span className="recipe-overview-chip">
                {ownedShoppingCount} vare{ownedShoppingCount === 1 ? '' : 'r'} fra gemte hjemmevarer
              </span>
            )}
            {(mergedPriceSummary.length > 0 || (!hasCompleteDynamicPricing && missingShoppingLinesCount > 0)) && (
              <details className="recipe-shopping-price-details">
                <summary>Se prisgrundlag</summary>
                <div>
                  {mergedPriceSummary.map((entry) => (
                    <span key={entry} className="recipe-overview-chip recipe-overview-chip-muted">{entry}</span>
                  ))}
                  {!hasCompleteDynamicPricing && missingShoppingLinesCount > 0 && (
                    <span className="recipe-overview-chip recipe-overview-chip-muted">
                      Prisen justeres for de varer, der har prisdata
                    </span>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      ) : (
        <p className="recipe-nutrition-empty">
          {normalize(publicPrice.pris_note) || 'Vi mangler prisdata for at vise et pålideligt prisoverblik.'}
        </p>
      )}
      <div className="recipe-shopping-grid">
        {shoppingDisplayItems.length > 0 && (
          <article className="recipe-shopping-card recipe-shopping-card-primary">
            <div className="recipe-shopping-card-header">
              <h4>Til kurven</h4>
              <span>{shoppingCountLabel}</span>
            </div>
            {renderShoppingChecklist(shoppingDisplayItems)}
          </article>
        )}
        {basis.length > 0 && (
          <article className="recipe-shopping-card recipe-shopping-card-secondary">
            <div className="recipe-shopping-card-header">
              <h4>Krydderiskab</h4>
              <span>{basis.length} varer</span>
            </div>
            {renderPriorityList(
              basis,
              formatBasisMeta,
              'recipe-clean-list recipe-clean-list-compact'
            )}
            <div className="recipe-price-notes recipe-shopping-card-notes">
              <p className="recipe-price-note">
                Olie, salt, peber og krydderier regnes som basisvarer og er ikke med i prisen.
              </p>
            </div>
          </article>
        )}
      </div>
    </section>
  );

  const renderLeftoverSection = () => (
    <section className={`recipe-detail-section recipe-leftovers-section${isCookingMode ? ' is-cooking-completion' : ''}`}>
      <div className="recipe-section-header">
        <h3>{isCookingMode ? 'Afslut madlavningen' : 'Rester bagefter'}</h3>
        <p>{inventoryMode === 'smart'
          ? (isCookingMode
            ? 'Når retten er lavet, opdaterer ét tryk både det brugte og de sandsynlige rester i dit spisekammer.'
            : 'Fortæl blot, når retten er lavet. Så opdaterer Budgetvenlig resten.')
          : 'Gem de rester, du har tilbage efter madlavningen, så næste opskrift kan tage højde for dem.'}</p>
      </div>
      {inventoryMode === 'smart' && (
        <article className={`recipe-smart-pantry-card${smartPantryUpdated ? ' is-complete' : ''}`}>
          <span className="recipe-pantry-use-icon" aria-hidden="true">
            {smartPantryUpdated ? <Check2Circle /> : <LightningCharge />}
          </span>
          <span className="recipe-pantry-use-copy">
            <strong>{smartPantryUpdated ? 'Spisekammeret er opdateret' : 'Smart registrering'}</strong>
            <small>
              {smartPantryUpdated
                ? 'Forbrug og sandsynlige rester er beregnet. Du kan rette estimaterne i Dit spisekammer.'
                : `${matchedPantryItems.length} lagervarer trækkes fra, og ${leftoverRows.length} sandsynlige rester gemmes automatisk.`}
            </small>
          </span>
          <button type="button" onClick={handleSmartPantryUpdate} disabled={smartPantryUpdated}>
            {smartPantryUpdated ? <Check2 size={15} aria-hidden="true" /> : <LightningCharge size={15} aria-hidden="true" />}
            {smartPantryUpdated
              ? 'Registreret'
              : (isCookingMode ? 'Retten er lavet · opdater spisekammer' : 'Retten er lavet')}
          </button>
        </article>
      )}
      {inventoryMode === 'precise' && (matchedPantryItems.length > 0 || pantryUsageConfirmed) && (
        <article className={`recipe-pantry-use-card${pantryUsageConfirmed ? ' is-confirmed' : ''}`}>
          <span className="recipe-pantry-use-icon" aria-hidden="true">
            {pantryUsageConfirmed ? <Check2Circle /> : <HouseDoorFill />}
          </span>
          <span className="recipe-pantry-use-copy">
            <strong>{pantryUsageConfirmed ? 'Varerne er registreret som brugt' : 'Brugt fra dit spisekammer'}</strong>
            <small>
              {pantryUsageConfirmed
                ? 'Uåbnede mængder er beholdt. Gem de faktiske rester nedenfor.'
                : `${matchedPantryItems.map((item) => item.productName).slice(0, 3).join(', ')}${matchedPantryItems.length > 3 ? ` +${matchedPantryItems.length - 3}` : ''}`}
            </small>
          </span>
          {!pantryUsageConfirmed && (
            <button type="button" onClick={handleConfirmPantryUsage}>
              <Check2 size={15} aria-hidden="true" />
              {isCookingMode
                ? `Retten er lavet · markér ${matchedPantryItems.length} som brugt`
                : `Markér ${matchedPantryItems.length} som brugt`}
            </button>
          )}
        </article>
      )}
      {pantryFlowStatus && (
        <p className="recipe-pantry-flow-status" role="status" aria-live="polite">
          {pantryFlowStatus}
        </p>
      )}
      <article className={`recipe-shopping-card recipe-leftovers-card${inventoryMode === 'precise' && requiresPantryUsageConfirmation ? ' is-blocked' : ''}`}>
        <div className="recipe-shopping-card-header">
          <h4>Til næste opskrift</h4>
          <span className="recipe-leftover-header-actions">
            <span>{inventoryMode === 'smart' ? 'Automatisk estimat' : `${savedLeftoverCount}/${leftoverRows.length} gemt`}</span>
            {inventoryMode === 'precise' && leftoverRows.length > 0 && (
              <button
                type="button"
                className="recipe-leftover-save-all-button"
                onClick={handleSaveAllLeftovers}
                disabled={requiresPantryUsageConfirmation || areAllLeftoversSaved}
              >
                <Check2Circle size={15} aria-hidden="true" />
                {areAllLeftoversSaved ? 'Alle rester gemt' : 'Gem alle rester'}
              </button>
            )}
          </span>
        </div>
        {inventoryMode === 'precise' && requiresPantryUsageConfirmation && (
          <p className="recipe-leftover-gate-note">
            Markér først varerne ovenfor som brugt. Derefter kan de beregnede rester lægges tilbage i spisekammeret.
          </p>
        )}
        {renderLeftoverOwnershipList(inventoryMode === 'smart')}
        <div className="recipe-price-notes recipe-shopping-card-notes">
          <p className="recipe-price-note">
            {inventoryMode === 'smart'
              ? 'Automatiske rester er estimater. De vises tydeligt i Dit spisekammer og kan altid rettes.'
              : 'Gemte rester vises i Dit spisekammer og kan bruges som hjemmevarer i næste opskrift.'}
          </p>
          <Link to="/pantry" className="recipe-leftover-pantry-link">
            <span>Gå til dit spisekammer</span>
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </article>
    </section>
  );

  return (
    <Container className={`recipe-view-page editorial-page ${isCookingMode ? 'recipe-cooking-mode' : ''}`}>
      <div className="recipe-back-link-row">
        <Link to="/recipes" className="recipe-back-link">Tilbage til opskrifter</Link>
      </div>

      {isCookingMode && (
        <section className="recipe-cook-mode-toolbar">
          <div className="recipe-cook-mode-summary">
            <span>Madlavningstilstand</span>
            {hasInstructions ? (
              <strong>{completedStepCount}/{instructionSteps.length} trin færdige</strong>
            ) : (
              <strong>Opskriften er klar</strong>
            )}
            {hasInstructions && (
              <span className="recipe-cook-mode-progress-track" aria-hidden="true">
                <span style={{ width: `${cookingProgressPercentage}%` }} />
              </span>
            )}
          </div>
          <button
            type="button"
            className={`recipe-cook-mode-toggle ${isCookingMode ? 'active' : ''}`}
            onClick={() => setIsCookingMode((prev) => !prev)}
          >
            Luk madlavningstilstand
          </button>
          {isCookingMode && wakeLockMessage && (
            <p className="recipe-cook-mode-progress">{wakeLockMessage}</p>
          )}
        </section>
      )}

      {!isCookingMode && (
        <section
          className={`recipe-hero-card ${hasHeroImage ? 'has-image' : 'is-image-fallback'}`}
        >
          <div className="recipe-hero-media">
            {hasHeroImage ? (
              <img
                src={heroImageSrc}
                alt=""
                aria-hidden="true"
                className="recipe-hero-image"
                data-image-index="0"
                data-image-candidates={JSON.stringify(heroImageCandidates)}
                style={{ objectPosition: heroImagePosition }}
                onError={(event) => {
                  const imageElement = event.currentTarget;
                  const candidates = JSON.parse(imageElement.dataset.imageCandidates || '[]');
                  const currentIndex = Number(imageElement.dataset.imageIndex || '0');
                  const nextIndex = currentIndex + 1;

                  if (nextIndex < candidates.length) {
                    imageElement.dataset.imageIndex = `${nextIndex}`;
                    imageElement.src = candidates[nextIndex];
                    return;
                  }

                  imageElement.onerror = null;
                  imageElement.src = PLACEHOLDER_IMAGE;
                }}
              />
            ) : (
              <div aria-hidden="true" className="recipe-hero-image is-image-fallback" />
            )}
            <div
              aria-hidden="true"
              className={`recipe-hero-overlay ${hasHeroImage ? '' : 'is-image-fallback'}`.trim()}
            />
            {weeklyPrice && (
              <div className="recipe-hero-deal-wrap">
                <div className="recipe-hero-deal-pair">
                  <span className="recipe-hero-deal-price">
                    <small>{displayedPriceLabel}</small>
                    <strong>{displayedWeeklyPrice}</strong>
                  </span>
                  {(dynamicSavingsPrice || savingsPrice) && (
                    <span className="recipe-hero-deal-savings">Spar {dynamicSavingsPrice || savingsPrice}</span>
                  )}
                </div>
                {hasCompleteDynamicPricing && dynamicNormalPrice && (
                  <small className="recipe-hero-deal-note">
                    {`Normalpris ca. ${dynamicNormalPrice}`}
                  </small>
                )}
              </div>
            )}
          </div>
          <div className={`recipe-hero-summary ${hasHeroImage ? 'has-image' : 'is-image-fallback'}`.trim()}>
            <div className="recipe-hero-summary-main">
              <div className="recipe-hero-heading-block">
                {featuredOfferNames.length > 0 && (
                  <p className="recipe-hero-kicker">
                    Bygger på {featuredOfferNames.join(', ')}
                  </p>
                )}
                <h1>{recipe.name}</h1>
                {renderFlavorText()}
              </div>
              <div className="recipe-hero-meta-row">
                {timeRange && (
                  <div className="recipe-meta-chip">
                    <Clock size={16} />
                    <span>{timeRange}</span>
                  </div>
                )}
                {normalize(recipe.portions) && (
                  <div className="recipe-meta-chip">
                    <People size={16} />
                    <span>{normalize(recipe.portions)} pers.</span>
                  </div>
                )}
              </div>
              <div className="recipe-hero-actions">
                <button
                  type="button"
                  className="recipe-cook-mode-toggle"
                  onClick={() => setIsCookingMode((prev) => !prev)}
                >
                  Start madlavningstilstand
                </button>
                <FavoriteButton
                  recipeId={recipe.recipe_id}
                  title={recipe.name}
                  image={heroImageSrc}
                />
              </div>
            </div>
            {(requiredGearItems.length > 0 || optionalGearItems.length > 0) && (
              <div className="recipe-hero-gear-grid">
                {requiredGearItems.map((gearItem) => {
                  const GearIcon = resolveGearIcon(gearItem);
                  return (
                    <div className="recipe-gear-card" key={`required-${gearItem}`}>
                      <GearIcon size={18} />
                      <small>Skal bruges</small>
                      <span>{gearItem}</span>
                    </div>
                  );
                })}
                {optionalGearItems.map((gearItem) => {
                  const GearIcon = resolveGearIcon(gearItem);
                  return (
                    <div className="recipe-gear-card" key={`optional-${gearItem}`}>
                      <GearIcon size={18} />
                      <small>Valgfrit</small>
                      <span>{gearItem}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {!isCookingMode && (
        <section className="recipe-standalone-actions" aria-label="Handlinger for denne opskrift">
          <div className="recipe-standalone-actions-copy">
            <strong>Denne ret alene</strong>
            <span>Lav en separat indkøbsliste.</span>
          </div>
          <div className="recipe-standalone-action-buttons">
            <button
              type="button"
              className="is-primary"
              onClick={handleOpenRecipeShoppingList}
              aria-controls="recipe-shopping-list"
              disabled={requiredTilKurv.length === 0 && ekstraOptional.length === 0}
            >
              <Basket3 size={16} aria-hidden="true" />
              Indkøbsliste
            </button>
            <button
              type="button"
              className="is-secondary"
              onClick={handleShareRecipeShoppingList}
              disabled={requiredTilKurv.length === 0 && ekstraOptional.length === 0}
            >
              <Share size={16} aria-hidden="true" />
              Del liste
            </button>
            <button type="button" className="is-secondary" onClick={handlePrintRecipe}>
              <Printer size={16} aria-hidden="true" />
              Print opskrift
            </button>
          </div>
          {recipeActionStatus && (
            <p className="recipe-standalone-action-status" role="status" aria-live="polite">
              {recipeActionStatus}
            </p>
          )}
        </section>
      )}

      {!isCookingMode && nutritionItems.length > 0 && (
        <details className="recipe-nutrition-disclosure">
          <summary>
            <span>Næring pr. portion</span>
            <strong>{nutritionItems.find((item) => item.label === 'Kcal')?.displayValue || `${nutritionItems.length} værdier`}</strong>
          </summary>
          <div className="recipe-nutrition-band" aria-label="Næring pr. portion">
            <div className="recipe-nutrition-band-grid">
              {nutritionItems.map((item) => (
                <div key={item.label} className="recipe-nutrition-band-stat">
                  <span className="recipe-nutrition-band-stat-label">{item.label}</span>
                  <span className="recipe-nutrition-band-stat-value">{item.displayValue}</span>
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      <Row className="g-3 recipe-view-layout">
        <Col xs={12} lg={isCookingMode ? 8 : 12} className={isCookingMode ? 'recipe-cooking-main' : ''}>
          {!isCookingMode && (requiredTilKurv.length > 0 || basis.length > 0 || ekstraOptional.length > 0) && (
            renderPrioritySection()
          )}

          {hasInstructions && (
            <section className="recipe-detail-section recipe-instructions-section">
              <div className="recipe-section-header">
                <h3>{isCookingMode ? 'Lav retten trin for trin' : 'Sådan gør du'}</h3>
                {isCookingMode && (
                  <p>Markér trinnene undervejs. Når maden er klar, afslutter du med at opdatere spisekammeret nedenfor.</p>
                )}
              </div>
              <ol className="recipe-steps-list">
                {instructionSteps.map((step, index) => (
                  <li
                    key={`recipe-step-${index}`}
                    className={`recipe-step-card ${completedSteps[index] ? 'is-completed' : ''}`}
                  >
                    <span className="recipe-step-index">{index + 1}</span>
                    <p className="recipe-step-text">{step}</p>
                    {isCookingMode && (
                      <label className="recipe-step-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(completedSteps[index])}
                          onChange={() => toggleStep(index)}
                          aria-label={`Markér trin ${index + 1} som færdigt`}
                        />
                        <span className="recipe-step-toggle-visual" aria-hidden="true">
                          <Check2 size={15} />
                        </span>
                      </label>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {renderLeftoverSection()}

        </Col>

        <Col xs={12} lg={4} className={isCookingMode ? 'recipe-cooking-sidebar' : 'd-none'}>
          <div className="recipe-sidebar-stack">
            {isCookingMode && requiredTilKurv.length > 0 && (
              <section className="recipe-detail-section recipe-sidebar-panel">
                <h3>{isCookingMode ? 'Det skal du bruge nu' : 'Indkøb (tilbud)'}</h3>
                <ul className="recipe-clean-list">
                  {visibleShoppingList.map((ingredient, index) => {
                    const priceLine = priceLineByProduct.get(normalize(ingredient.produkt_navn).toLowerCase());
                    return (
                      <li key={`${ingredient.produkt_navn}-${index}`}>
                        <strong>{getIngredientDisplayName(ingredient, priceLine)}</strong>
                        {buildInlineShoppingMeta(ingredient) ? <span>{buildInlineShoppingMeta(ingredient)}</span> : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default RecipeView;


